'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { LogOut, ChevronRight, BookOpen, AlertCircle, Loader2 } from 'lucide-react';
import SubmissionForm from './SubmissionForm';
import SubmissionStatus from './SubmissionStatus';
import { useAuth } from '@/contexts/AuthContext';
import { thesisService } from '@/lib/services/thesisService';
import { createClient } from '@/lib/supabase/client';

export type SubmissionStep = 'form' | 'status';

export default function StudentPortalShell() {
  const { user, loading, signOut, signInWithMicrosoft, signInWithGoogle } = useAuth();
  const [step, setStep] = useState<SubmissionStep>('form');
  const [existingSubmission, setExistingSubmission] = useState<any>(null);
  const [loadingSubmission, setLoadingSubmission] = useState(false);
  const [userProfile, setUserProfile] = useState<any>(null);

  useEffect(() => { if (user) loadUserData(); }, [user]);

  const loadUserData = async () => {
    if (!user) return;
    setLoadingSubmission(true);
    try {
      const supabase = createClient();
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();
      setUserProfile(profile);

      const submission = await thesisService.getMySubmission(user.id);
      if (submission) {
        setExistingSubmission(submission);
        setStep('status');
      }
    } catch (err) {
      console.log('Load user data error:', err);
    } finally {
      setLoadingSubmission(false);
    }
  };

  const handleSubmissionComplete = async () => {
    const submission = await thesisService.getMySubmission(user?.id);
    setExistingSubmission(submission);
    setStep('status');
  };

  const handleSignOut = async () => {
    try { await signOut(); } catch (err) { console.log('Sign out error:', err); }
    finally { window.location.assign('/public-thesis-portal'); }
  };

  const displayName = userProfile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Student';
  const displayEmail = user?.email || '';
  const displayDept = userProfile?.department || user?.user_metadata?.department || '';
  const initials = displayName.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();

  if (!loading && !user) {
    return (
      <div className="min-h-screen bg-[#f7f8fc] flex items-center justify-center" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <div className="bg-white border border-[#e2e6f0] rounded-2xl shadow-sm p-8 w-full max-w-md">
          <div className="flex flex-col items-center mb-6">
            <AppLogo size={48} />
            <h1 className="text-xl font-700 text-[#0f1a35] mt-3">Student Portal</h1>
            <p className="text-sm text-[#4a5578] mt-1 text-center">Sign in with your university account to submit your thesis</p>
          </div>
          <div className="space-y-3">
            <button onClick={() => signInWithMicrosoft('/student-submission-portal')}
              className="w-full flex items-center justify-center gap-3 border border-[#e2e6f0] rounded-xl px-4 py-3 text-sm font-600 text-[#0f1a35] hover:bg-[#f7f8fc] transition-all">
              <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              Sign in with Microsoft (Azure AD)
            </button>
            <button onClick={() => signInWithGoogle('/student-submission-portal')}
              className="w-full flex items-center justify-center gap-3 border border-[#e2e6f0] rounded-xl px-4 py-3 text-sm font-600 text-[#0f1a35] hover:bg-[#f7f8fc] transition-all">
              <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Sign in with Google
            </button>
          </div>
          <p className="text-xs text-[#8892a8] text-center mt-4">Use your university email account to access the portal</p>
        </div>
      </div>
    );
  }

  if (loading || loadingSubmission) {
    return (
      <div className="min-h-screen bg-[#f7f8fc] flex items-center justify-center" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={32} className="animate-spin text-[#1a2e5a]" />
          <p className="text-sm text-[#4a5578]">Loading your portal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f7f8fc]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <header className="bg-[#1a2e5a] border-b border-[#243668] sticky top-0 z-40">
        <div className="max-w-screen-2xl mx-auto px-6 xl:px-10 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link href="/public-thesis-portal" className="flex items-center gap-2.5">
              <AppLogo size={30} />
              <div className="hidden sm:block">
                <div className="text-white font-600 text-sm leading-tight">Philips University</div>
                <div className="text-[#c9a84c] text-xs leading-tight">Student Portal</div>
              </div>
            </Link>
            <div className="hidden md:flex items-center gap-1 ml-4 text-xs text-white/40">
              <Link href="/public-thesis-portal" className="hover:text-white/70 transition-colors">Repository</Link>
              <ChevronRight size={10} />
              <span className="text-white/60">Thesis Submission</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-2.5">
              <div className="w-7 h-7 rounded-full bg-[#c9a84c]/20 border border-[#c9a84c]/40 flex items-center justify-center">
                <span className="text-[#c9a84c] text-xs font-700">{initials}</span>
              </div>
              <div>
                <div className="text-white text-xs font-600 leading-tight">{displayName}</div>
                <div className="text-white/40 text-xs leading-tight">{displayEmail}</div>
              </div>
            </div>
            <button onClick={handleSignOut}
              className="flex items-center gap-1.5 text-white/50 hover:text-white border border-white/15 hover:border-white/30 px-3 py-1.5 rounded-lg text-xs font-500 transition-all">
              <LogOut size={12} /> Sign Out
            </button>
          </div>
        </div>
      </header>

      {!existingSubmission && step !== 'status' && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="max-w-screen-2xl mx-auto px-6 xl:px-10 py-2.5 flex items-center gap-2">
            <AlertCircle size={14} className="text-amber-600 flex-shrink-0" />
            <p className="text-xs text-amber-800 font-500">
              <strong>Important:</strong> You are allowed only one thesis submission. Please review all fields carefully before submitting. |{' '}
              <strong>Σημαντικό:</strong> Επιτρέπεται μόνο μία υποβολή διατριβής.
            </p>
          </div>
        </div>
      )}

      <main className="max-w-screen-2xl mx-auto px-6 xl:px-10 2xl:px-16 py-6">
        {step === 'form' && (
          <SubmissionForm
            userEmail={displayEmail}
            userName={displayName}
            department={displayDept}
            userId={user?.id}
            onComplete={handleSubmissionComplete}
          />
        )}
        {step === 'status' && (
          <SubmissionStatus
            status={existingSubmission?.thesis_status ?? 'submitted'}
            comment={existingSubmission?.sent_back_comment ?? ''}
            onResubmit={() => setStep('form')}
          />
        )}
      </main>
    </div>
  );
}
